import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, Trash2, ExternalLink, Link as LinkIcon, FileText, Sparkles } from "lucide-react";
import { MemoryBookMediaTile } from "@/components/MemoryBookMediaTile";

/**
 * Onglet complet Médiathèque + Sources externes + Faits manuels pour l'atelier MemoryBook.
 * Ce composant est utilisé dans AdminMemoryBookEditorPage.
 */
export function MemoryBookMediaLibrary({ requestId, editable = true, onCorpusChanged }) {
  const [medias, setMedias] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]);
  const [manualFacts, setManualFacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [m, l, f] = await Promise.all([
        api.get(`/admin/memory-books/requests/${requestId}/medias`),
        api.get(`/admin/memory-books/requests/${requestId}/external-links`),
        api.get(`/admin/memory-books/requests/${requestId}/manual-facts`),
      ]);
      setMedias(m.data.items || []);
      setExternalLinks(l.data.items || []);
      setManualFacts(f.data.items || []);
      onCorpusChanged?.({
        medias: m.data.items || [],
        external_links: l.data.items || [],
        manual_facts: f.data.items || [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger la médiathèque");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [requestId]);

  const deleteMedia = async (media) => {
    if (!window.confirm("Supprimer ce média définitivement ?")) return;
    try {
      await api.delete(`/admin/memory-books/medias/${media.id}`);
      toast.success("Média supprimé.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const deleteExternalLink = async (link) => {
    if (!window.confirm("Supprimer cette source externe ?")) return;
    try {
      await api.delete(`/admin/memory-books/external-links/${link.id}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const deleteManualFact = async (fact) => {
    if (!window.confirm("Supprimer ce fait manuel ?")) return;
    try {
      await api.delete(`/admin/memory-books/manual-facts/${fact.id}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement de la médiathèque…
      </div>
    );
  }

  const counts = {
    medias_total: medias.length,
    medias_validated: medias.filter((m) => ["AUTHORIZED", "LICENSED", "INTERNAL"].includes(m.rights_status)).length,
    medias_pending: medias.filter((m) => ["TO_VERIFY", "UNKNOWN"].includes(m.rights_status)).length,
  };

  return (
    <div className="space-y-6" data-testid="mb-media-library">
      {/* Section médias */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm flex items-center gap-2">
              Médiathèque du livret
              <Badge variant="outline" data-testid="mb-media-count">{counts.medias_validated} validés</Badge>
              {counts.medias_pending > 0 && (
                <Badge variant="secondary">{counts.medias_pending} à valider</Badge>
              )}
            </CardTitle>
            {editable && (
              <div className="flex items-center gap-2">
                <DiscoverImagesFromUrlDialog requestId={requestId} onCreated={refresh} />
                <AddExternalMediaDialog requestId={requestId} onCreated={refresh} />
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Toute image externe DOIT être validée avant d'être incluse dans le PDF. Une copie interne est alors téléchargée et figée.
          </p>
        </CardHeader>
        <CardContent>
          {medias.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              Aucun média pour l'instant. Cliquez sur « Proposer une image externe » pour en ajouter,
              ou construisez le snapshot pour importer les photos internes de l'athlète.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {medias.map((m) => (
                <MemoryBookMediaTile
                  key={m.id}
                  media={m}
                  disabled={!editable}
                  onChange={() => refresh()}
                  onDelete={deleteMedia}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section liens externes */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <LinkIcon className="h-4 w-4" /> Sources externes textuelles
              <Badge variant="outline">{externalLinks.length}</Badge>
            </CardTitle>
            {editable && (
              <AddExternalLinkDialog requestId={requestId} onCreated={refresh} />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Articles de presse, résultats officiels, sites institutionnels. Ces sources nourrissent
            le contexte fourni à l'IA (jamais consultées par le modèle lui-même).
          </p>
        </CardHeader>
        <CardContent>
          {externalLinks.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">Aucune source externe ajoutée.</div>
          ) : (
            <ul className="space-y-2">
              {externalLinks.map((lnk) => (
                <li key={lnk.id} className="flex items-start justify-between gap-3 text-sm border rounded p-2">
                  <div className="min-w-0">
                    <a href={lnk.url} target="_blank" rel="noreferrer" className="font-medium text-[hsl(var(--aito-lagoon))] hover:underline inline-flex items-center gap-1">
                      {lnk.title || lnk.url} <ExternalLink className="h-3 w-3" />
                    </a>
                    {lnk.source_name && (
                      <div className="text-xs text-muted-foreground">{lnk.source_name}</div>
                    )}
                    {lnk.summary && (
                      <div className="text-xs mt-1 line-clamp-2">{lnk.summary}</div>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteExternalLink(lnk)} disabled={!editable}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Section faits manuels */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" /> Faits manuels sourcés
              <Badge variant="outline">{manualFacts.length}</Badge>
            </CardTitle>
            {editable && (
              <AddManualFactDialog requestId={requestId} onCreated={refresh} />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Informations vérifiées par l'Admin (sélection, distinction, palmarès manquant). Chaque fait
            requiert obligatoirement une URL source.
          </p>
        </CardHeader>
        <CardContent>
          {manualFacts.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">Aucun fait manuel ajouté.</div>
          ) : (
            <ul className="space-y-2">
              {manualFacts.map((f) => (
                <li key={f.id} className="flex items-start justify-between gap-3 text-sm border rounded p-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{f.kind}</div>
                    <div className="font-medium">{f.text}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Source :{" "}
                      <a href={f.source_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] hover:underline">
                        {f.source_label || f.source_url}
                      </a>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteManualFact(f)} disabled={!editable}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ dialogs

function AddExternalMediaDialog({ requestId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    original_image_url: "",
    source_page_url: "",
    role: "gallery",
    caption: "",
    photographer: "",
    organization: "",
    credit: "",
    license: "",
    consulted_at: new Date().toISOString().slice(0, 10),
  });

  const submit = async () => {
    if (!form.original_image_url && !form.source_page_url) {
      toast.error("Renseignez au moins une URL (image ou page source).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/memory-books/requests/${requestId}/medias`, {
        source_type: "EXTERNAL_URL",
        rights_status: "TO_VERIFY",
        ...form,
      });
      toast.success("Média proposé — à valider avant utilisation.");
      setOpen(false);
      setForm({ ...form, original_image_url: "", source_page_url: "", caption: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec ajout média");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="mb-add-external-media">
        <Plus className="h-4 w-4 mr-1" /> Proposer une image externe
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Proposer une image externe</DialogTitle>
            <DialogDescription>
              L'image n'est pas téléchargée à ce stade. Elle apparaîtra avec le statut « À vérifier »
              jusqu'à validation. Le téléchargement local et le figeage n'ont lieu qu'à la validation.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">URL directe de l'image *</Label>
              <Input
                data-testid="mb-external-image-url"
                value={form.original_image_url}
                onChange={(e) => setForm({ ...form, original_image_url: e.target.value })}
                placeholder="https://media.exemple.pf/photo.jpg"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">URL de la page source</Label>
              <Input
                data-testid="mb-external-page-url"
                value={form.source_page_url}
                onChange={(e) => setForm({ ...form, source_page_url: e.target.value })}
                placeholder="https://tahiti-infos.com/article-original"
              />
            </div>
            <div>
              <Label className="text-xs">Rôle prévu</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cover">Couverture</SelectItem>
                  <SelectItem value="portrait">Portrait</SelectItem>
                  <SelectItem value="action">Action</SelectItem>
                  <SelectItem value="podium">Podium</SelectItem>
                  <SelectItem value="gallery">Galerie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Consultée le</Label>
              <Input type="date" value={form.consulted_at}
                onChange={(e) => setForm({ ...form, consulted_at: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Légende (visible sur le PDF)</Label>
              <Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Photographe</Label>
              <Input value={form.photographer} onChange={(e) => setForm({ ...form, photographer: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Média / Organisme</Label>
              <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Crédit à afficher</Label>
              <Input value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })}
                placeholder="© Jean Dupont / Tahiti Sport Presse" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Licence / mention légale (si connue)</Label>
              <Input value={form.license} onChange={(e) => setForm({ ...form, license: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={busy} data-testid="mb-external-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ajouter comme candidat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AddExternalLinkDialog({ requestId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ url: "", title: "", summary: "", source_name: "" });

  const submit = async () => {
    if (!form.url) { toast.error("URL requise."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/memory-books/requests/${requestId}/external-links`, {
        ...form,
        consulted_at: new Date().toISOString(),
      });
      toast.success("Source externe ajoutée.");
      setOpen(false);
      setForm({ url: "", title: "", summary: "", source_name: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec ajout source");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="mb-add-external-link">
        <Plus className="h-4 w-4 mr-1" /> Ajouter une source
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une source externe</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">URL *</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://tahiti-infos.com/article" data-testid="mb-extlink-url" />
            </div>
            <div>
              <Label className="text-xs">Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Média / Source</Label>
              <Input value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })}
                placeholder="Tahiti Infos" />
            </div>
            <div>
              <Label className="text-xs">Résumé (facultatif)</Label>
              <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AddManualFactDialog({ requestId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ kind: "note", text: "", source_url: "", source_label: "" });

  const submit = async () => {
    if (!form.text || !form.source_url) {
      toast.error("Le texte du fait ET l'URL source sont obligatoires.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/memory-books/requests/${requestId}/manual-facts`, form);
      toast.success("Fait manuel ajouté.");
      setOpen(false);
      setForm({ kind: "note", text: "", source_url: "", source_label: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec ajout fait");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="mb-add-manual-fact">
        <Plus className="h-4 w-4 mr-1" /> Ajouter un fait
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un fait sourcé</DialogTitle>
            <DialogDescription>
              Ce fait sera intégré au corpus IA. Une URL source vérifiable est OBLIGATOIRE :
              rien ne doit être écrit sans provenance identifiable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Note factuelle</SelectItem>
                  <SelectItem value="achievement">Palmarès</SelectItem>
                  <SelectItem value="competition">Compétition</SelectItem>
                  <SelectItem value="quote">Citation (avec source publiée)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fait *</Label>
              <Textarea rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Sélection en équipe territoriale pour la saison 2025." data-testid="mb-fact-text" />
            </div>
            <div>
              <Label className="text-xs">URL source *</Label>
              <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                placeholder="https://ftva.pf/selection-2025" data-testid="mb-fact-source-url" />
            </div>
            <div>
              <Label className="text-xs">Libellé de la source</Label>
              <Input value={form.source_label} onChange={(e) => setForm({ ...form, source_label: e.target.value })}
                placeholder="Fédération Tahitienne de Va'a" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={busy} data-testid="mb-fact-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ajouter le fait
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ==========================================================================
// V11.2 — Découverte d'images depuis une URL (workflow hybride)
// --------------------------------------------------------------------------
// L'admin colle une URL d'article/page ; le backend scrape og:image + <img>
// et crée un média EXTERNAL_URL en TO_VERIFY pour chaque candidat.
// L'admin valide ensuite chaque image (avec crédit + licence obligatoires)
// via la tuile MemoryBookMediaTile (workflow existant).
// ==========================================================================
function DiscoverImagesFromUrlDialog({ requestId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [maxImages, setMaxImages] = useState(6);
  const [result, setResult] = useState(null);

  const run = async () => {
    const url = (sourceUrl || "").trim();
    if (!url) {
      toast.error("Colle une URL http/https à scanner.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post(
        `/admin/memory-books/requests/${requestId}/discover-image-candidates`,
        {
          source_url: url,
          auto_add: true,
          max_images: Math.max(1, Math.min(12, parseInt(maxImages, 10) || 6)),
        },
      );
      setResult(res.data || {});
      const nCand = (res.data?.candidates || []).length;
      const nCreated = (res.data?.created_media_ids || []).length;
      if (nCand === 0) {
        toast.info("Aucune image détectée sur cette page.");
      } else {
        toast.success(`${nCand} image(s) proposée(s) — ${nCreated} ajoutée(s) au workflow (statut À vérifier).`);
        onCreated?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la découverte");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="mb-discover-images-btn"
      >
        <Sparkles className="h-4 w-4 mr-1" /> Découvrir des images
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="mb-discover-images-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[hsl(var(--aito-gold))]" />
              Découvrir des images depuis une URL
            </DialogTitle>
            <DialogDescription>
              Colle l'URL d'une page (article, galerie, page source) : nous en extrayons
              l'image de couverture (og:image) et les images de contenu. Chaque candidat
              est ajouté au livret avec le statut « À vérifier » — tu valides ensuite
              chaque image (crédit + licence obligatoires) avant qu'elle ne soit
              téléchargée et figée.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-2 items-end">
            <div>
              <Label className="text-xs">URL de la page</Label>
              <Input
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://tahiti-infos.com/article-source"
                data-testid="mb-discover-images-url"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
              />
            </div>
            <div>
              <Label className="text-xs">Max</Label>
              <Input
                type="number"
                min={1}
                max={12}
                value={maxImages}
                onChange={(e) => setMaxImages(e.target.value)}
                className="w-24"
                data-testid="mb-discover-images-max"
              />
            </div>
          </div>

          {result && (result.candidates || []).length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs text-muted-foreground mb-2">
                {(result.candidates || []).length} candidat(s) détecté(s) ·
                {" "}{(result.created_media_ids || []).length} ajouté(s) au workflow (statut « À vérifier »).
                Valide chaque image dans la médiathèque avec crédit + licence.
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[45vh] overflow-y-auto pr-1">
                {(result.candidates || []).map((c, i) => (
                  <div key={i} className="rounded border overflow-hidden" data-testid={`mb-discover-cand-${i}`}>
                    <div className="aspect-video bg-secondary flex items-center justify-center overflow-hidden">
                      <img
                        src={c.image_url}
                        alt={c.alt || ""}
                        className="w-full h-full object-cover object-top"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                    <div className="p-1.5 text-[10px] text-muted-foreground truncate">
                      {c.is_og && <Badge variant="outline" className="text-[9px] mr-1">og:image</Badge>}
                      {c.alt || c.image_url}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Fermer</Button>
            <Button
              onClick={run}
              disabled={busy || !sourceUrl.trim()}
              className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
              data-testid="mb-discover-images-run"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Lancer la découverte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
